import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ScrollText, Save, Eye, History, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDERS = ["{agent_name}", "{customer_name}", "{amount}", "{reference}", "{order_number}", "{items}", "{delivery_date}"];
const LANGS = [["en", "English"], ["ar", "Arabic"], ["hi", "Hindi"]];

/** Settings → Call Scripts: one script per Type/Status, greetings, EN/AR/HI bodies, internal note, preview, versions. */
export default function CallScriptsManager() {
  const { activeSlug } = useClient();
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("leads.manage_fields");
  const [scripts, setScripts] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [lang, setLang] = useState("en");
  const [preview, setPreview] = useState(null);
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState({ lead_type: "", lead_status: "" });

  const load = useCallback(() => {
    api.get("/call-scripts").then((r) => {
      setScripts(r.data.scripts || []);
      if (!sel && r.data.scripts?.length) setSel(r.data.scripts[0].id);
    }).catch(() => toast.error("Failed to load scripts"));
    api.get("/lead-dropdowns").then((r) => {
      setTypes(r.data.grouped?.lead_type || []);
      setStatuses(r.data.grouped?.lead_status || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const s = scripts.find((x) => x.id === sel);
    setDraft(s ? JSON.parse(JSON.stringify(s)) : null);
    setPreview(null); setVersions(null);
  }, [sel, scripts]);

  const labelOf = (rows, v) => (rows.find((r) => r.value === v) || {}).label || v;

  const saveScript = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/call-scripts/${draft.id}`, {
        name: draft.name, greetings: draft.greetings, body: draft.body,
        internal_note: draft.internal_note, published: draft.published,
      });
      toast.success(`Script saved · v${data.script.version} — agents see it on their next lead`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post(`/call-scripts/${draft.id}/preview`);
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const loadVersions = async () => {
    const { data } = await api.get(`/call-scripts/${draft.id}/versions`);
    setVersions(data.versions || []);
  };

  const createScript = async () => {
    if (!newKey.lead_type || !newKey.lead_status) return;
    try {
      const { data } = await api.post("/call-scripts", {
        ...newKey, name: `${labelOf(types, newKey.lead_type)} · ${labelOf(statuses, newKey.lead_status)}`,
        greetings: { morning: "Good morning {customer_name}, this is {agent_name} calling from Starlink Qatar.",
                     afternoon: "Good afternoon {customer_name}, this is {agent_name} calling from Starlink Qatar.",
                     evening: "Good evening {customer_name}, this is {agent_name} calling from Starlink Qatar." },
        body: { en: "", ar: "", hi: "" }, internal_note: "", published: false,
      });
      toast.success("Script created — fill in the wording and publish");
      setAdding(false); setSel(data.script.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4" data-testid="call-scripts-manager">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><ScrollText className="w-4 h-4 text-slate-500" /> Call scripts</span>
            {canEdit && (
              <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => setAdding((a) => !a)} data-testid="script-add-btn">
                <Plus className="w-3 h-3 mr-1" /> New
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {adding && (
            <div className="border border-slate-200 rounded p-2 space-y-2 mb-2" data-testid="script-add-form">
              <Select value={newKey.lead_type} onValueChange={(v) => setNewKey((k) => ({ ...k, lead_type: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="script-add-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={newKey.lead_status} onValueChange={(v) => setNewKey((k) => ({ ...k, lead_status: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="script-add-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{statuses.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" className="h-7 text-xs w-full" onClick={createScript} disabled={!newKey.lead_type || !newKey.lead_status}
                      data-testid="script-add-confirm">Create</Button>
            </div>
          )}
          {scripts.map((s) => (
            <button key={s.id} type="button" onClick={() => setSel(s.id)}
                    className={`w-full text-left rounded px-2 py-1.5 border text-xs transition-colors ${sel === s.id ? "border-indigo-300 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`script-item-${s.lead_status}`}>
              <div className="font-medium text-slate-800 truncate">{s.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{labelOf(types, s.lead_type)} · {labelOf(statuses, s.lead_status)}</div>
              <div className="flex gap-1 mt-1">
                <Badge variant="outline" className="text-[9px]">v{s.version}</Badge>
                {!s.published && <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300">draft</Badge>}
              </div>
            </button>
          ))}
          {scripts.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No scripts yet.</div>}
        </CardContent>
      </Card>

      {draft ? (
        <Card data-testid="script-editor">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between gap-2 flex-wrap">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={!canEdit}
                     className="h-8 text-sm font-semibold max-w-md" data-testid="script-name" />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                  <Switch checked={!!draft.published} onCheckedChange={(v) => setDraft({ ...draft, published: v })} disabled={!canEdit}
                          data-testid="script-published" /> Published
                </label>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={runPreview} data-testid="script-preview-btn">
                  <Eye className="w-3 h-3 mr-1" /> Preview
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={loadVersions} data-testid="script-versions-btn">
                  <History className="w-3 h-3 mr-1" /> History
                </Button>
                {canEdit && (
                  <Button size="sm" className="h-7 text-[10px] bg-indigo-600 hover:bg-indigo-700" onClick={saveScript} disabled={busy}
                          data-testid="script-save-btn">
                    {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            <div className="text-[10px] text-slate-500">
              Placeholders: {PLACEHOLDERS.map((p) => <code key={p} className="bg-slate-100 rounded px-1 mx-0.5">{p}</code>)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {["morning", "afternoon", "evening"].map((g) => (
                <div key={g}>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400">Greeting · {g}</Label>
                  <Textarea rows={2} value={draft.greetings?.[g] || ""} disabled={!canEdit} className="mt-1 text-xs resize-none"
                            onChange={(e) => setDraft({ ...draft, greetings: { ...draft.greetings, [g]: e.target.value } })}
                            data-testid={`script-greeting-${g}`} />
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Script body</Label>
                <Tabs value={lang} onValueChange={setLang}>
                  <TabsList className="h-7 bg-slate-100 p-0.5">
                    {LANGS.map(([k, l]) => (
                      <TabsTrigger key={k} value={k} className="text-[10px] h-6 px-2" data-testid={`script-body-lang-${k}`}>
                        {l}{draft.body?.[k] ? "" : " ·"}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <Textarea rows={7} value={draft.body?.[lang] || ""} disabled={!canEdit} dir={lang === "ar" ? "rtl" : "ltr"}
                        className="mt-1 text-xs" placeholder={lang === "en" ? "What the agent reads out" : "Optional translation — leave blank to hide this language on the call screen"}
                        onChange={(e) => setDraft({ ...draft, body: { ...draft.body, [lang]: e.target.value } })}
                        data-testid="script-body-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Internal note — suggested action (agent only, not read out)</Label>
              <Textarea rows={3} value={draft.internal_note || ""} disabled={!canEdit} className="mt-1 text-xs"
                        onChange={(e) => setDraft({ ...draft, internal_note: e.target.value })} data-testid="script-internal-note-input" />
            </div>

            {preview && (
              <div className="border border-emerald-200 bg-emerald-50 rounded p-3 space-y-1.5" data-testid="script-preview">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Preview · sample lead: {preview.sample_lead}</div>
                <p className="font-medium text-slate-800">{preview.greetings?.morning}</p>
                <p className="whitespace-pre-wrap text-slate-800">{preview.body?.[lang] || preview.body?.en}</p>
                {preview.internal_note && <p className="text-indigo-800 text-[11px]">Internal: {preview.internal_note}</p>}
              </div>
            )}
            {versions && (
              <div className="border border-slate-200 rounded p-2 space-y-1" data-testid="script-versions">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Version history</div>
                {versions.length === 0 && <div className="text-slate-400">No earlier versions.</div>}
                {versions.map((v) => (
                  <div key={v.version} className="flex items-start justify-between gap-2 border-t border-slate-100 pt-1">
                    <div className="min-w-0">
                      <span className="font-mono text-[10px] text-slate-500">v{v.version} · {v.by} · {v.at ? new Date(v.at).toLocaleString() : ""}</span>
                      <div className="text-slate-600 truncate max-w-xl">{v.body?.en}</div>
                    </div>
                    {canEdit && (
                      <Button variant="ghost" size="sm" className="h-6 text-[10px]"
                              onClick={() => setDraft({ ...draft, greetings: v.greetings, body: v.body, internal_note: v.internal_note })}
                              data-testid={`script-restore-${v.version}`}>Restore to editor</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card><CardContent className="p-6 text-xs text-slate-400">Select a script to edit.</CardContent></Card>
      )}
    </div>
  );
}
